import React, { useMemo, useCallback } from 'react';
import { Box, VStack, Heading, HStack, Icon, Tag, Menu, Tooltip, Checkbox } from '@televet/kibble-ui';
import { AutomationsListItemFragment, useGetAutomationParentAutomationsLazyQuery } from 'shared/types/graphql';
import { useHistory } from 'react-router-dom';
import { formattedSendingHours } from 'pages/Automations';
import { AutomationEditorView } from 'pages/Automations/types/AutomationEditorView';
import { useDuplicateOneAutomation, useIsChildAutomation } from 'pages/Automations/hooks';
import { useGetFieldVisibility } from 'pages/Automations/hooks/useGetFieldVisibility';
import { useAuth } from 'shared/providers/AuthProvider';
import { useCurrentAutomationId } from 'pages/Automations/hooks/useCurrentAutomationId';
import { useGetAutomationsBasePath } from 'pages/Automations/hooks/useGetAutomationsBasePath';
import { AutomationToDelete } from '../Modals/DeleteAutomationModal';

export enum AutomationsListItemActions {
  Delete = 'delete',
  Duplicate = 'duplicate',
}

interface AutomationsListItemProps
  extends Pick<AutomationsListItemFragment, 'id' | 'name' | 'event' | 'triggerType' | 'isPublished' | 'sendingHours'> {
  isSelectingAutomations: boolean;
  isSelected: boolean;
  hasError: boolean;
  onDeleteAutomation: (automation: AutomationToDelete) => void;
  onToggleSelectionMode: () => void;
  onToggleSelection: () => void;
}

export const AutomationsListItem = (props: AutomationsListItemProps): JSX.Element => {
  const { isSelectingAutomations, isSelected, hasError, onDeleteAutomation, onToggleSelection, ...automation } = props;
  const { isImpersonating } = useAuth();
  const currentAutomationId = useCurrentAutomationId();
  const { id, name, event: automationType, triggerType, isPublished, sendingHours } = automation;
  const { handleDuplicateAutomation } = useDuplicateOneAutomation({ id });
  const history = useHistory();
  const isChildAutomation = useIsChildAutomation({ triggerType, automationType });
  const fieldIsVisible = useGetFieldVisibility(automationType, triggerType);
  const { automationsBasePath } = useGetAutomationsBasePath();

  const isCurrentlyActive = useMemo(() => {
    return currentAutomationId === id;
  }, [currentAutomationId, id]);

  const handleAutomationItemClick = useCallback(
    (e): void => {
      if (isImpersonating && (e.metaKey || e.ctrlKey || isSelectingAutomations)) {
        onToggleSelection();
        return;
      }

      // Prevent item selection if user is clicking the menu button
      if (e.target.type === 'button') return;
      history.push(`${automationsBasePath}/${id}/${AutomationEditorView.Settings}`);
    },
    [isImpersonating, isSelectingAutomations, history, automationsBasePath, id, onToggleSelection],
  );

  const [getParentAutomations, { data: parentAutomationsData }] = useGetAutomationParentAutomationsLazyQuery();

  const handleOnMenuOpen = useCallback(async () => {
    if (isChildAutomation) {
      await getParentAutomations({
        variables: { workflowId: id },
      });
    }
  }, [getParentAutomations, id, isChildAutomation]);

  const isDeleteDisabled = useMemo(() => {
    if (!parentAutomationsData?.findManyParentWorkflowEventSetting) return false;

    // Prevent users from deleting a child automation if it is linked to other automations
    return isChildAutomation && parentAutomationsData?.findManyParentWorkflowEventSetting?.length > 0;
  }, [isChildAutomation, parentAutomationsData]);

  const handleDeleteAutomation = useCallback((): void => {
    onDeleteAutomation({ id, name });
  }, [id, name, onDeleteAutomation]);

  const menuItems = useMemo(() => {
    return [
      {
        label: 'Duplicate Automation',
        value: AutomationsListItemActions.Duplicate,
        onSelect: handleDuplicateAutomation,
      },
      {
        label: (
          <Tooltip
            titleCaseLabel={false}
            placement="right"
            label={
              isDeleteDisabled ? 'This automation cannot be deleted until all linked automations have been removed' : ''
            }
          >
            <Box w="100%">Delete Automation</Box>
          </Tooltip>
        ),
        value: AutomationsListItemActions.Delete,
        onSelect: handleDeleteAutomation,
        isDisabled: isDeleteDisabled,
      },
    ];
  }, [isDeleteDisabled, handleDeleteAutomation, handleDuplicateAutomation]);

  return (
    <HStack
      className="Automations__SidePanelListItem"
      bg="background.default"
      w="100%"
      py={2}
      px={3}
      alignItems="flex-start"
      justifyContent="flex-start"
      cursor="pointer"
      borderRadius="lg"
      id={`automation-${id}`}
      scrollMargin={3}
      borderWidth="3px"
      borderColor={hasError ? 'border.danger' : isCurrentlyActive || isSelected ? 'border.info' : 'transparent'}
      _hover={{
        borderColor: hasError
          ? 'border.dangerContrast'
          : isCurrentlyActive || isSelected
          ? 'border.info'
          : 'border.default',
      }}
      onClick={handleAutomationItemClick}
    >
      {isSelectingAutomations ? (
        <Box flex="0 0 auto" alignSelf="center">
          <Checkbox isChecked={isSelected} onChange={onToggleSelection} />
        </Box>
      ) : null}
      <Box w={8} className="Automations__SidePanelListItem-Icon">
        <Icon name={isChildAutomation ? 'chainLink' : 'robot'} size="xl" />
      </Box>
      <VStack alignItems="flex-start" flexGrow={3}>
        <Heading size="xs" variant="default" className="Automations__SidePanelListItem-Title">
          {name}
        </Heading>
        <HStack className="Automations__SidePanelListItem-Tags">
          <Tag
            label={isPublished ? 'Active' : 'Inactive'}
            backgroundColor={isPublished ? 'background.success' : 'background.alpha'}
            color={isPublished ? 'text.success' : 'text.default'}
            size="sm"
          />
          {fieldIsVisible.sendHours && (
            <Tag
              label={formattedSendingHours[sendingHours]}
              variant="info"
              leftElement={<Icon name="clock" size="xs" variant="info" />}
              size="sm"
            />
          )}
        </HStack>
      </VStack>
      {!isSelectingAutomations && (
        <Menu
          buttonProps={{
            variant: 'ghostNeutral',
            size: 'xs',
            leftIconName: 'dotsVertical',
            showRightIcon: false,
          }}
          containerProps={{
            onOpen: handleOnMenuOpen,
          }}
          listProps={{
            menuItems,
          }}
        />
      )}
    </HStack>
  );
};
